import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import debounce from "lodash/debounce";
import "./GeneralInvoiceFormStyles.css";
import { ICreditor } from "src/stores/krediflow/InvoiceTypes";
import { INVOICE_PROPERTY } from "src/stores/krediflow/InvoiceEnums";
import styles from "./CreditorFormFields.module.css";
import { ColorStyle } from "src/utils/Colors";
import { AiCreditorHint } from "src/screens/creditors/creditor/details/form/fields/AiCreditorHint";
import { AiFieldFilledHint } from "src/screens/creditors/creditor/details/form/fields/AiFieldFilledHint";

const CreditorFormFieldsBase = () => {
    const { ticketStore, uiStore, contactPersonSearchStore, creditorSearchStore, invoiceStore } =
        useContext(RootStoreContext);

    const [displayCreditorList, setDisplayCreditorList] = useState<boolean>(false);
    const [lastScrollPositionYPCreditorItems, setLastSrollYPositionCreditorItems] = useState<number>(0);

    const creditorInputRef = useRef<HTMLInputElement>(null);
    const creditorFieldRef = useRef<HTMLDivElement>(null);

    const resetSearchQuery = useCallback(() => {
        creditorSearchStore.setCurrentCreditorSearchQuery(creditorSearchStore.selectedCreditorQueryString ?? "");
    }, [creditorSearchStore]);

    const aiDetection = invoiceStore.currentInvoice?.aiDetection;
    const aiCreditorDetection = aiDetection?.results.creditorid;
    const aiCreditorOutcome = aiCreditorDetection?.outcome;

    // AI creditor candidates shown at the top of the dropdown for an ambiguous
    // outcome, but only while unresolved (acceptedsource null) and the user hasn't
    // typed. Resolved async via a by-ids query (creditor master data may be
    // backend-searched rather than fully loaded).
    const [aiCreditorCandidates, setAiCreditorCandidates] = useState<ICreditor[]>([]);

    useEffect(() => {
        let isCancelled = false;

        const query = creditorSearchStore.currentCreditorSearchQuery ?? "";
        const isDefaultView = query === creditorSearchStore.selectedCreditorQueryString || query === "";
        const isUnresolved = (aiCreditorDetection?.acceptedsource ?? null) === null;

        if (!aiCreditorDetection || aiCreditorOutcome !== "ambiguous" || !isUnresolved || !isDefaultView) {
            setAiCreditorCandidates([]);
            return;
        }

        creditorSearchStore
            .getCreditorDataByIds(aiCreditorDetection.candidates.map((candidate) => candidate.entityid))
            .then((creditors) => {
                if (!isCancelled) setAiCreditorCandidates(creditors);
            })
            .catch((error) => {
                console.error("Failed to resolve AI creditor candidates:", error);
                if (!isCancelled) setAiCreditorCandidates([]);
            });

        return () => {
            isCancelled = true;
        };
    }, [
        aiCreditorDetection,
        aiCreditorOutcome,
        creditorSearchStore,
        creditorSearchStore.currentCreditorSearchQuery,
        creditorSearchStore.selectedCreditorQueryString
    ]);

    const aiCreditorCandidateIdSet = useMemo(
        () => new Set(aiCreditorCandidates.map((creditor) => creditor.id)),
        [aiCreditorCandidates]
    );
    // Same rule as the realestate list: reserve the indicator slot only when
    // the list holds AI candidates to align.
    const showAiCandidateSlot = aiCreditorCandidateIdSet.size > 0;

    // AI candidates first, then the regular (filtered) list with AI ids removed.
    const mergedCreditorList: ICreditor[] = useMemo(() => {
        if (aiCreditorCandidates.length === 0) {
            return creditorSearchStore.filteredCreditorData;
        }
        return [
            ...aiCreditorCandidates,
            ...creditorSearchStore.filteredCreditorData.filter((creditor) => !aiCreditorCandidateIdSet.has(creditor.id))
        ];
    }, [aiCreditorCandidates, aiCreditorCandidateIdSet, creditorSearchStore.filteredCreditorData]);

    useEffect(() => {
        // Containment instead of a class whitelist: the list closes on this mousedown, before the
        // row's click fires, so any unlisted element inside a row (e.g. a layout wrapper) swallowed the selection.
        const handleClickOutsidePersonInput = (event: MouseEvent) => {
            if (
                displayCreditorList &&
                creditorFieldRef.current &&
                !creditorFieldRef.current.contains(event.target as Node)
            ) {
                setDisplayCreditorList(false);

                if (
                    invoiceStore.currentInvoice?.isCreditorSelected &&
                    creditorSearchStore.currentCreditorSearchQuery !== creditorSearchStore.selectedCreditorQueryString
                ) {
                    resetSearchQuery();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutsidePersonInput);

        return () => {
            document.removeEventListener("mousedown", handleClickOutsidePersonInput);
        };
    }, [
        displayCreditorList,
        ticketStore,
        resetSearchQuery,
        creditorSearchStore.currentCreditorSearchQuery,
        creditorSearchStore.selectedCreditorQueryString,
        invoiceStore.currentInvoice?.isCreditorSelected
    ]);

    useEffect(() => {
        if (creditorSearchStore.triggerFocusOnCreditorInput) {
            creditorInputRef.current?.focus();
            creditorSearchStore.setTriggerFocusOnCreditorInput(false);
        }
        // This useEffect only listens to changes in contactPersonSearchStore.triggerFocusOnPersonInput
        // eslint-disable-next-line
    }, [contactPersonSearchStore.triggerFocusOnPersonInput]);

    const updateInvoiceStoreWithSelectedCreditor = useCallback(
        async (creditor: ICreditor | undefined) => {
            invoiceStore.updateInvoiceStoreWithSelectedCreditor(creditor);
        },
        [invoiceStore]
    );

    const onSelectCreditorItem = useCallback(
        (activeListItemIndex: number) => {
            const selectedCreditorItem = mergedCreditorList[activeListItemIndex];

            // User actively picked something — clear the auto-applied flag so the banner disappears.
            invoiceStore.currentInvoice?.setCreditorAutoAppliedByAi(false);
            updateInvoiceStoreWithSelectedCreditor(selectedCreditorItem);
        },
        [mergedCreditorList, updateInvoiceStoreWithSelectedCreditor, invoiceStore.currentInvoice]
    );

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "creditor",
        totalNumberOfDisplayedItems: invoiceStore.numberOfDisplayedItems,
        setIsListDisplayed: setDisplayCreditorList,
        isListDisplayed: displayCreditorList,
        onSelectItem: onSelectCreditorItem,
        resetSearchQuery: resetSearchQuery,
        focusNextOnEnter: true
    });

    const resetAmountOfItemsDisplayedInCreditorList = useCallback(() => {
        setLastSrollYPositionCreditorItems(0);
        invoiceStore.setNumberOfDisplayedItems(invoiceStore.sliceSizeOfDisplayedItems);
    }, [invoiceStore]);

    const searchCreditor = debounce((query: string) => {
        creditorSearchStore.searchCreditorsInBackendByQuery(query);
    }, 800);

    const onChangeCreditorInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            resetAmountOfItemsDisplayedInCreditorList();

            const inputCreditor = e.target.value;
            creditorSearchStore.setCurrentCreditorSearchQuery(inputCreditor);

            if (creditorSearchStore.isSearchingCreditorsInBackendByQuery) {
                if (inputCreditor.length > 2) {
                    searchCreditor(inputCreditor);
                } else {
                    creditorSearchStore.setCreditorData([]);
                }
            }
        },

        // searchCreditor function cannot be in dependency array, because it is a debounced function
        // eslint-disable-next-line
        [contactPersonSearchStore, resetAmountOfItemsDisplayedInCreditorList]
    );

    const onFocusCreditorInput = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (invoiceStore.currentInvoice?.isCreditorSelected && creditorInputRef.current) {
                creditorInputRef.current.select();
            }

            resetAmountOfItemsDisplayedInCreditorList();
            setDisplayCreditorList(true);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [
            resetAmountOfItemsDisplayedInCreditorList,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            invoiceStore.currentInvoice?.isCreditorSelected
        ]
    );

    const onClickCreditorItem = useCallback(
        (personId: string) => async (e: any) => {
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
            setDisplayCreditorList(false);

            const selectedCreditorItem = mergedCreditorList.find((creditor) => {
                return creditor.personId === personId;
            });

            // User actively picked something — clear the auto-applied flag so the banner disappears.
            invoiceStore.currentInvoice?.setCreditorAutoAppliedByAi(false);
            updateInvoiceStoreWithSelectedCreditor(selectedCreditorItem);
        },
        [
            updateInvoiceStoreWithSelectedCreditor,
            setActiveListItemIndex,
            NO_LIST_ITEM_SELECTED,
            mergedCreditorList,
            invoiceStore.currentInvoice
        ]
    );

    const onScrollCreditorList = useCallback(
        (e: React.BaseSyntheticEvent) => {
            const windowHeight = e.target.clientHeight;
            const pixelsFromTop = e.target.scrollTop;
            const totalHeight = e.target.scrollHeight;

            const is150PixelsFromBottom = totalHeight - pixelsFromTop < windowHeight + 150;
            const isScrollingDown = pixelsFromTop > lastScrollPositionYPCreditorItems;

            const hasMoreResulsToDisplay = invoiceStore.numberOfDisplayedItems < mergedCreditorList.length;

            if (is150PixelsFromBottom && isScrollingDown && hasMoreResulsToDisplay) {
                invoiceStore.setNumberOfDisplayedItems(
                    invoiceStore.numberOfDisplayedItems + invoiceStore.sliceSizeOfDisplayedItems
                );
            }

            setLastSrollYPositionCreditorItems(pixelsFromTop);
        },
        [lastScrollPositionYPCreditorItems, mergedCreditorList.length, invoiceStore]
    );

    const getSpanElementWithFormattedText = useCallback(
        (stringToFormat: string) => {
            const formattedString =
                creditorSearchStore.currentCreditorSearchQuery !== ""
                    ? boldenMatchedSubstring(
                          creditorSearchStore.currentCreditorSearchQuery ?? "",
                          stringToFormat,
                          "highlighted-text creditor"
                      )
                    : stringToFormat;

            return (
                <span
                    className="creditor-list-item-span"
                    dangerouslySetInnerHTML={{
                        __html: formattedString
                    }}
                />
            );
        },
        [creditorSearchStore.currentCreditorSearchQuery]
    );

    const creditorSelectionList = (
        <div id="creditor-dropdown-list-container">
            <div
                className={`${styles.CreditorSelectionListSearchIndicator} ${
                    !creditorSearchStore.isSearchingCreditorData &&
                    !(creditorSearchStore.creditorData.length === 0 && !creditorSearchStore.isSearchingCreditorData)
                        ? styles.NoBorderBottom
                        : ""
                }`}
            >
                {creditorSearchStore.isSearchingCreditorData ? (
                    <UI.RotatingSpinner noLogo size={30} className={styles.SearchingCreditorsSpinner} />
                ) : undefined}
            </div>
            {creditorSearchStore.isSearchingCreditorData ? undefined : (
                <ul id="creditor-dropdown-list" className={"dropdown-list creditor"} onScroll={onScrollCreditorList}>
                    {mergedCreditorList.slice(0, invoiceStore.numberOfDisplayedItems).map((creditor, index) => {
                            const creditorName = `${`${creditor.name1}${
                                creditor.name2 ? ` ${creditor.name2}` : ""
                            }`.trim()}${
                                creditor.number !== undefined && creditor.number !== 0 ? ` [${creditor.number}]` : ""
                            }`;

                            const creditorZipCity = `${creditor.zip ?? ""} ${creditor.city ?? ""}`.trim();

                            const formattedCreditorNameSpan = getSpanElementWithFormattedText(creditorName ?? "");

                            const formattedCreditorStreetSpan = creditor.street
                                ? getSpanElementWithFormattedText(creditor.street ?? "")
                                : undefined;

                            const formattedCreditorZipCitySpan = creditorZipCity
                                ? getSpanElementWithFormattedText(creditorZipCity ?? "")
                                : undefined;

                            const formattedCreditorEmailSpan = creditor.email
                                ? getSpanElementWithFormattedText(creditor.email)
                                : undefined;

                            const formattedCreditorMobileSpan = creditor.mobile
                                ? getSpanElementWithFormattedText(creditor.mobile)
                                : undefined;

                            const isAiCandidate = aiCreditorCandidateIdSet.has(creditor.id);

                            const creditorItemContent = uiStore.isMobile ? (
                                <>
                                    <div>{formattedCreditorNameSpan}</div>
                                    <div>{formattedCreditorStreetSpan}</div>
                                    <div>{formattedCreditorZipCitySpan}</div>
                                    <div>{formattedCreditorEmailSpan}</div>
                                    <div>{formattedCreditorMobileSpan}</div>
                                </>
                            ) : (
                                <>
                                    <div className={"creditor-list-item-line-1"}>
                                        {formattedCreditorNameSpan}
                                        {formattedCreditorStreetSpan === undefined &&
                                        formattedCreditorZipCitySpan === undefined
                                            ? ""
                                            : ", "}
                                        {formattedCreditorStreetSpan}
                                        {formattedCreditorStreetSpan === undefined ? "" : ", "}
                                        {formattedCreditorZipCitySpan}
                                    </div>
                                    <div className={"creditor-list-item-line-2"}>
                                        {formattedCreditorEmailSpan}
                                        {formattedCreditorEmailSpan === undefined ||
                                        formattedCreditorMobileSpan === undefined
                                            ? ""
                                            : formattedCreditorEmailSpan !== undefined &&
                                              formattedCreditorMobileSpan !== undefined
                                            ? ", "
                                            : ""}
                                        {formattedCreditorMobileSpan}
                                    </div>
                                </>
                            );

                            return (
                                <li key={index}>
                                    <div
                                        className={`list-item creditor ${uiStore.isMobile ? "mobile" : ""}`}
                                        id={`creditor-list-item-${index}`}
                                        onClick={onClickCreditorItem(creditor.personId ?? "")}
                                    >
                                        <div className={styles.CreditorItemLayout}>
                                            {showAiCandidateSlot && (
                                                <span
                                                    className={styles.AiCandidateIndicator}
                                                    aria-hidden={!isAiCandidate}
                                                >
                                                    {isAiCandidate && (
                                                        <UI.Icon
                                                            icon={UI.SVGIcon.AiSparkle}
                                                            size={22}
                                                            color={ColorStyle("primary")}
                                                        />
                                                    )}
                                                </span>
                                            )}
                                            <div className={styles.CreditorItemContent}>{creditorItemContent}</div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                </ul>
            )}
        </div>
    );

    const currentCreditor = invoiceStore.currentInvoice?.creditor;

    const currentCreditorName = currentCreditor
        ? `${currentCreditor?.name1}${`${currentCreditor?.name2 ? ` ${currentCreditor?.name2}` : ""}`}`.trim()
        : undefined;

    const removeSelectedCreditor = useCallback(() => {
        updateInvoiceStoreWithSelectedCreditor(undefined);
    }, [updateInvoiceStoreWithSelectedCreditor]);

    const creditorElements = (
        <UI.Container fluid className="mt-3">
            <UI.Row>
                {currentCreditorName && currentCreditorName.length > 0 ? (
                    <div className="d-flex align-items-center w-100 justify-content-between">
                        {currentCreditor?.number !== undefined && currentCreditor?.number !== 0 ? (
                            <div className="d-flex align-items-center">
                                <div className={`${styles.CreditorName}`}>{currentCreditorName}</div>
                                <div className="d-flex ml-2">[{currentCreditor?.number}]</div>
                            </div>
                        ) : (
                            <div className={`${styles.CreditorName} mb-1`}>{currentCreditorName}</div>
                        )}
                        {invoiceStore.isEditing ? (
                            <div style={{ cursor: "pointer" }} onClick={removeSelectedCreditor}>
                                <UI.Icon icon={UI.SVGIcon.Trash} color={ColorStyle("primary")} size={"medium"} />
                            </div>
                        ) : undefined}
                    </div>
                ) : undefined}
            </UI.Row>
            <UI.Row>
                {currentCreditor?.street || currentCreditor?.zip || currentCreditor?.city ? (
                    <div className={styles.CreditorInfo}>
                        <div className={styles.CreditorInfoIcon}>
                            <UI.Icon icon={UI.SVGIcon.MapPin} color={ColorStyle("primary")} size={25} />
                        </div>
                    </div>
                ) : undefined}
                {currentCreditor?.street ? <div className="mb-1">{currentCreditor?.street}</div> : undefined}
                {currentCreditor?.street && (currentCreditor?.zip || currentCreditor?.city) ? (
                    <span className="mr-1" />
                ) : (
                    ""
                )}
                {currentCreditor?.zip || currentCreditor?.city ? (
                    <div>{`${currentCreditor?.zip} ${currentCreditor?.city}`.trim()}</div>
                ) : undefined}
            </UI.Row>

            <UI.Row>
                {currentCreditor?.email ? (
                    <div className={styles.CreditorInfo}>
                        <div className={styles.CreditorInfoIcon}>
                            <UI.Icon icon={UI.SVGIcon.Mail} color={ColorStyle("primary")} size={"medium"} />
                        </div>
                        <div>
                            <a style={{ color: "#212529" }} href={`mailto:${currentCreditor?.email}`}>
                                {currentCreditor?.email}
                            </a>
                        </div>
                    </div>
                ) : undefined}
            </UI.Row>
            <UI.Row>
                {currentCreditor?.mobile ? (
                    <div className={`${styles.CreditorInfo}`}>
                        <div className={styles.CreditorInfoIcon}>
                            <UI.Icon icon={UI.SVGIcon.Device} color={ColorStyle("primary")} size={"medium"} />
                        </div>
                        <div>
                            <a style={{ color: "#212529" }} href={`tel:${currentCreditor?.mobile}`}>
                                {currentCreditor?.mobile}
                            </a>
                        </div>
                    </div>
                ) : undefined}
            </UI.Row>
            <UI.Row>
                {currentCreditor?.phonebusiness ? (
                    <div className={`${styles.CreditorInfo}`}>
                        <div className={styles.CreditorInfoIcon}>
                            <UI.Icon icon={UI.SVGIcon.Phone} color={ColorStyle("primary")} size={25} />
                        </div>
                        <div>
                            <a style={{ color: "#212529" }} href={`tel:${currentCreditor?.phonebusiness}`}>
                                {currentCreditor?.phonebusiness}
                            </a>
                        </div>
                    </div>
                ) : undefined}
            </UI.Row>
        </UI.Container>
    );

    const creditorSearchInput = (
        <UI.Input
            ref={creditorInputRef}
            type="text"
            autoComplete="off"
            className={styles.CreditorInputField}
            id="ticket-form-peson-input"
            value={creditorSearchStore.currentCreditorSearchQuery ?? ""}
            onChange={onChangeCreditorInput}
            onFocus={onFocusCreditorInput}
            onKeyDown={onKeyDownFunction}
            disabled={!invoiceStore.isEditing}
            iconRight={<UI.Icon icon={UI.SVGIcon.Search} color="grey" size={"small"} style={{ right: "5px" }} />}
        />
    );

    return invoiceStore.isEditing ? (
        <>
            <div className="position-relative" ref={creditorFieldRef}>
                {creditorSearchInput}
                {displayCreditorList ? creditorSelectionList : undefined}
            </div>
            <AiCreditorHint />
            <AiFieldFilledHint field={INVOICE_PROPERTY.CREDITOR} inFlow />
            {creditorElements}
        </>
    ) : (
        <>
            <AiCreditorHint />
            <AiFieldFilledHint field={INVOICE_PROPERTY.CREDITOR} inFlow />
            {creditorElements}
        </>
    );
};

export const CreditorFormFields = Sentry.withProfiler(observer(CreditorFormFieldsBase));
